import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const PricingPage = () => {
  const { t } = useTranslation();

  const plans = [
    {
      id: 'free',
      name: t('pricing.plans.free.name'),
      price: t('pricing.plans.free.price'),
      period: t('pricing.plans.free.period'),
      description: t('pricing.plans.free.description'),
      features: [
        t('pricing.plans.free.features.0'),
        t('pricing.plans.free.features.1'),
        t('pricing.plans.free.features.2'),
        t('pricing.plans.free.features.3')
      ],
      buttonText: t('pricing.plans.free.button'),
      popular: false,
      gradient: 'from-gray-400 to-gray-600'
    },
    {
      id: 'pro',
      name: t('pricing.plans.pro.name'),
      price: t('pricing.plans.pro.price'),
      period: t('pricing.plans.pro.period'),
      description: t('pricing.plans.pro.description'),
      features: [
        t('pricing.plans.pro.features.0'),
        t('pricing.plans.pro.features.1'),
        t('pricing.plans.pro.features.2'),
        t('pricing.plans.pro.features.3'),
        t('pricing.plans.pro.features.4'),
        t('pricing.plans.pro.features.5')
      ],
      buttonText: t('pricing.plans.pro.button'),
      popular: true,
      gradient: 'from-pink-400 to-pink-600'
    },
    {
      id: 'premium',
      name: t('pricing.plans.premium.name'),
      price: t('pricing.plans.premium.price'),
      period: t('pricing.plans.premium.period'),
      description: t('pricing.plans.premium.description'),
      features: [
        t('pricing.plans.premium.features.0'),
        t('pricing.plans.premium.features.1'),
        t('pricing.plans.premium.features.2'),
        t('pricing.plans.premium.features.3'),
        t('pricing.plans.premium.features.4'),
        t('pricing.plans.premium.features.5'),
        t('pricing.plans.premium.features.6')
      ],
      buttonText: t('pricing.plans.premium.button'),
      popular: false,
      gradient: 'from-purple-400 to-purple-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* 页面标题 */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        {/* 定价卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-xl overflow-hidden ${
                plan.popular ? 'ring-2 ring-pink-400 scale-105' : ''
              }`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              whileHover={{ y: -5 }}
            >
              {/* 热门标签 */}
              {plan.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="bg-gradient-to-r from-pink-400 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    {t('pricing.popular')}
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* 计划名称和价格 */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-slate-600 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center">
                    <span className={`text-5xl font-bold bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent`}>
                      {plan.price}
                    </span>
                    <span className="text-slate-600 ml-2">{plan.period}</span>
                  </div>
                </div>

                {/* 功能列表 */}
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* 选择按钮 */}
                <button
                  className={`w-full py-3 px-6 rounded-xl font-medium transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-pink-400 to-pink-600 text-white hover:from-pink-500 hover:to-pink-700 shadow-lg hover:shadow-xl'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {plan.buttonText}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 常见问题 */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h2 className="text-3xl font-bold text-slate-800 mb-8">
            {t('pricing.faq.title')}
          </h2>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {t('pricing.faq.q1.question')}
              </h3>
              <p className="text-slate-600">
                {t('pricing.faq.q1.answer')}
              </p>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {t('pricing.faq.q2.question')}
              </h3>
              <p className="text-slate-600">
                {t('pricing.faq.q2.answer')}
              </p>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {t('pricing.faq.q3.question')}
              </h3>
              <p className="text-slate-600">
                {t('pricing.faq.q3.answer')}
              </p>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {t('pricing.faq.q4.question')}
              </h3>
              <p className="text-slate-600">
                {t('pricing.faq.q4.answer')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;